/**
 * Pure calculation helpers extracted from PatientCreate (RR form).
 * Centralises all float boundary-rounding so behaviour is testable.
 */

export const r2 = (n: number): number => parseFloat(n.toFixed(2))

// ---------------------------------------------------------------------------
// VAT price for a single line item (used in submit items map)
// ---------------------------------------------------------------------------
export function computeItemVatPrice(subTotal: number, vat: number, vatType: string): number {
  if (vat === 99) return 0
  if (vatType === 'included') {
    return r2(subTotal - (subTotal * 100) / (100 + vat))
  }
  return r2(subTotal * (vat / 100))
}

// ---------------------------------------------------------------------------
// computeAutoVat — single source of truth for a line item's auto VAT amount.
//
// Mirrors the inline formula in every expense PatientCreate summary effect
// (`item.vat === 7 ? r2(subTotal * vat/100) : 0`, with the included variant
// `r2(quantity*(pricePerUnit-discountPerUnit) - subTotal)`). Only vat === 7
// carries VAT; 0 and 99 yield 0 — matching the summary inline exactly.
//
// `subTotal` is the stored line value: net (ex-VAT) base for both vatTypes
// (the form stores the ex-VAT amount as subTotal in included mode).
// excluded → r2(subTotal * vat/100); included → r2(gross - subTotal).
// ---------------------------------------------------------------------------
export interface AutoVatItem {
  subTotal: number
  vat: number | string
  quantity: number
  pricePerUnit: number
  discountPerUnit: number
}

export function computeAutoVat(item: AutoVatItem, vatType: string): number {
  if (Number(item.vat) !== 7) return 0
  if (vatType === 'included') {
    return r2(item.quantity * (item.pricePerUnit - item.discountPerUnit) - item.subTotal)
  }
  return r2(item.subTotal * (Number(item.vat) / 100))
}

export interface VatPriceItem extends AutoVatItem {
  vatPrice?: number | string | null
}

export function resolveItemVatPrice(item: VatPriceItem, vatType: string): number {
  const vatPrice = Number(item.vatPrice)
  if (Number.isFinite(vatPrice)) return r2(vatPrice)
  return computeAutoVat(item, vatType)
}

/**
 * Keep an included-VAT line's gross amount unchanged when its VAT is manually
 * overridden.  The difference becomes the new taxable base (net sales).
 * For excluded VAT, the taxable base is independent from the VAT amount.
 */
export function computeBaseAfterVatOverride(
  item: AutoVatItem,
  vatPrice: number,
  vatType: string
): number {
  if (vatType !== 'included') return r2(Number(item.subTotal) || 0)

  const gross = Number(item.quantity) * (Number(item.pricePerUnit) - Number(item.discountPerUnit))
  return r2(gross - Number(vatPrice || 0))
}

/** Restore the normal taxable base derived from the line price. */
export function computeAutoVatBase(item: AutoVatItem, vatType: string): number {
  const gross = Number(item.quantity) * (Number(item.pricePerUnit) - Number(item.discountPerUnit))
  if (vatType !== 'included' || Number(item.vat) !== 7) return r2(gross)

  return r2((gross * 100) / (100 + Number(item.vat)))
}

// ---------------------------------------------------------------------------
// productPriceInfo accumulation (mirrors the useEffect in PatientCreate)
// ---------------------------------------------------------------------------
export interface PriceItem {
  subTotal: number
  vat: number
  pricePerUnit: number
  discountPerUnit: number
  quantity: number
  whTaxPrice: number
}

export interface ProductPriceInfo {
  total: number
  subTotal: number
  discountTotal: number
  depositTotal: number
  beforeDepositTotal: number
  whTaxTotal: number
  vatTotal: number
  vatItemPriceTotal: number
  zeroVatItemPriceTotal: number
  noVatItemPriceTotal: number
}

export function computeProductPriceInfo(items: PriceItem[], vatType: string): ProductPriceInfo {
  let total = 0
  let subTotal = 0
  let discountTotal = 0
  let whTaxTotal = 0
  let vatTotal = 0
  let vatItemPriceTotal = 0
  let zeroVatItemPriceTotal = 0
  let noVatItemPriceTotal = 0

  for (const item of items) {
    if (!item.subTotal) continue

    let vt = item.vat === 7 ? r2(item.subTotal * (item.vat / 100)) : 0
    if (vatType === 'included') {
      vt =
        item.vat === 7
          ? r2(item.quantity * (item.pricePerUnit - item.discountPerUnit) - item.subTotal)
          : 0
    }

    total += item.subTotal
    subTotal += item.subTotal - item.whTaxPrice
    discountTotal += item.discountPerUnit * item.quantity
    whTaxTotal += item.whTaxPrice
    zeroVatItemPriceTotal += item.vat === 0 ? item.subTotal : 0
    noVatItemPriceTotal += item.vat === 99 ? item.subTotal : 0
    vatTotal += vt
    vatItemPriceTotal += item.vat === 7 ? item.subTotal : 0
  }

  const grandTotal = vatTotal + vatItemPriceTotal + noVatItemPriceTotal + zeroVatItemPriceTotal

  return {
    total: r2(grandTotal),
    subTotal: r2(grandTotal - whTaxTotal),
    discountTotal: r2(discountTotal),
    depositTotal: 0,
    beforeDepositTotal: 0,
    whTaxTotal: r2(whTaxTotal),
    vatTotal: r2(vatTotal),
    vatItemPriceTotal: r2(vatItemPriceTotal),
    zeroVatItemPriceTotal: r2(zeroVatItemPriceTotal),
    noVatItemPriceTotal: r2(noVatItemPriceTotal),
  }
}

// ---------------------------------------------------------------------------
// Submit-form helpers
// ---------------------------------------------------------------------------
export function computeRemainTotalAmount(total: number, whTaxPrice: number): number {
  return r2(total - (whTaxPrice || 0))
}

export function computeTotalAmountCondition(
  total: number,
  whTaxTotal: number,
  fees: number
): number {
  return r2(total - whTaxTotal - fees)
}

/**
 * Determines document state after submit.
 * Uses rounded comparison to avoid float equality failures.
 */
export function resolveDocumentState(
  paidAmount: number,
  totalAmountCondition: number,
  documentState: string,
  draftState: string,
  awaitReceiptState: string,
  outstandingState: string
): string {
  if (documentState === draftState) return draftState
  return r2(paidAmount) === r2(totalAmountCondition) ? awaitReceiptState : outstandingState
}

/**
 * Determines deduction document state.
 * Uses rounded comparison so values like 0.000001 are treated as 0.
 */
export function resolveDeductionState(
  remaining: number,
  paidState: string,
  outstandingState: string
): string {
  return r2(remaining) === 0 ? paidState : outstandingState
}

/**
 * Computes remaining amount after a payment deduction is applied.
 * Rounds at this boundary so the value stored to DB is clean.
 */
export function computePaymentRemaining(
  currentRemainOrTotal: number,
  amount: number,
  whTaxPrice: number
): number {
  return r2(currentRemainOrTotal - (Number(amount || 0) - Number(whTaxPrice || 0)))
}

/**
 * Computes remainTotalAmount for PO deduction in processItemsSequentially.
 */
export function computePoRemainTotalAmount(
  poTotal: number,
  cumulativeCurrent: number,
  previousOther: number
): number {
  return r2(poTotal - cumulativeCurrent - previousOther)
}
